import {ICartPosition, IMoney} from "@connox/typescript-library";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import {ICartRebateVoucher} from "@connox/typescript-library/types/cart/CartRebateVoucher";
import * as React from "react";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FaCheck, FaTrashAlt} from "react-icons/fa";
import ApplicableVouchersModal from "../../lib/ModalWindow/ApplicableVouchersModal";
import {isCart, useCartContext, useUnsafeCartContext} from "../CartContextProvider/CartContextProvider";
import * as Sentry from "@sentry/browser";
import {IPercent} from "@connox/typescript-library/types/cart/Percent";
import {VoucherType} from "@connox/typescript-library/types/cart/VoucherType";
import styles from "./VoucherBlock.module.scss";

const VoucherBlock: React.FC = () => {
  const context = useUnsafeCartContext();
  const {t} = useTranslation("cart");
  const voucherInputRef = useRef<HTMLInputElement>(null);
  // input value on init empty
  const [inputValue, setInputValue] = useState("");

  if (!context) {
    return null;
  }

  const SubmitVoucherLink = () => {
    // return nothing if input value is empty
    if (inputValue === "") {
      return null;
    }

    return (
      <span className={styles.redeemLink} onClick={submitVoucherCode}>
        {t("Einlösen")}
      </span>
    );
  };

  const submitVoucherCode = async (evt: React.MouseEvent<HTMLSpanElement> | React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (!voucherInputRef.current?.value) {
      return;
    }

    const voucherCode = voucherInputRef.current.value;

    if (isCart(context)) {
      await context.cart.addVoucher(voucherCode);
      if (voucherInputRef.current) {
        setInputValue("");
      }
    } else {
      // todo finish this
      try {
        const {cart, done} = await context.requestCart();
        await cart.addVoucher(voucherCode);
        if (voucherInputRef.current) {
          setInputValue("");
        }
        done();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  };

  return (
    <div className={styles.voucher}>
      <form onSubmit={submitVoucherCode}>
        <input
          value={inputValue}
          id="CartVoucherInput"
          type="text"
          name="VoucherInput"
          onChange={(e) => setInputValue(e.target.value)}
          ref={voucherInputRef}
          placeholder={t("Gutschein einlösen?")}
        />
        <SubmitVoucherLink />
      </form>
      {isCart(context) && <VoucherList />}
    </div>
  );
};

const EmptyVoucher: React.FC<{position: ICartPosition}> = (props) => {
  if (!props.position.reference) {
    return null;
  }
  const {voucherCode} = props.position.reference;
  const {messages} = props.position;
  const {text} = props.position;

  return (
    <VoucherLine
      code={voucherCode || ""}
      messages={messages}
      minimumOrderValueAbsolute={null}
      minimumOrderQuantityAbsolute={null}
      text={text}
      voucherAmount={null}
      voucherPercentage={null}
      type={null}
    />
  );
};

const VoucherSwitch: React.FC<{position: ICartPosition}> = (props) => {
  const {reference, text} = props.position;

  if (reference?.voucher) {
    return <AddApplicabilityMessages voucher={reference.voucher} text={text} />;
  }

  return <EmptyVoucher position={props.position} />;
};

/*
 * whole voucher list
 */
const VoucherList: React.FC = () => {
  const {cart} = useCartContext();
  const voucherLines = cart.positions.filter((pos: ICartPosition) => pos.type === "Voucher");

  /*
   * Remove double elements from object
   */
  const voucherLinesReduced = voucherLines.reduce((previous: ICartPosition[], current) => {
    if (!previous.some((pos) => pos.reference?.voucherCode === current.reference?.voucherCode)) {
      return previous.concat(current);
    } else {
      return previous;
    }
  }, []);

  const {rebates} = cart;

  return (
    <div className={styles.list}>
      {rebates.map((rebate) => (
        <VoucherLine key={rebate.code} {...rebate} text={rebate.code} />
      ))}
      {voucherLinesReduced.map((voucherLine, i) => (
        <VoucherSwitch key={voucherLine.reference?.voucherCode || `voucher_switch_${i}`} position={voucherLine} />
      ))}
    </div>
  );
};

/*
 * fill applicability messages to cart position type voucher
 */
const AddApplicabilityMessages: React.FC<{text: string; voucher: ICartRebateVoucher}> = (props: {
  text: string;
  voucher: ICartRebateVoucher;
}) => {
  const {cart} = useCartContext();
  const {text, voucher} = props;
  const {messages, type} = voucher;
  const applicabilityMessages: ICartMessage[] = [];

  const cartItemList = cart.positions.filter<ICartPosition>(
    (pos): pos is ICartPosition => !!pos.reference?.itemId && !!pos.item,
  );
  const relativeVoucherApplicableItems = cartItemList.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isRelativeVoucherApplicable,
  );
  const fixedVoucherApplicableItems = cartItemList.filter<ICartPosition>(
    (pos): pos is ICartPosition => pos.discountability.isFixedVoucherApplicable,
  );

  if (type === "Fix") {
    // fixed voucher applicability
    if (!fixedVoucherApplicableItems.length) {
      // not any applicable item found
      applicabilityMessages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
    } else if (cartItemList.length !== fixedVoucherApplicableItems.length) {
      // some, but not every item applicable
      applicabilityMessages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
    }
    // else, all items are applicable (no message added)
  } else if (type === "Relative") {
    // relative voucher applicability
    if (!relativeVoucherApplicableItems.length) {
      // not any applicable item found
      applicabilityMessages.push({code: NO_VOUCHER_APPLICABLE_ITEM_FOUND, field: "", message: "", type: "error"});
    } else if (cartItemList.length !== relativeVoucherApplicableItems.length) {
      // some, but not every item applicable
      applicabilityMessages.push({code: NOT_ALL_ITEMS_VOUCHER_APPLICABLE, field: "", message: "", type: "warning"});
    }
    // else, all items are applicable (no message added)
  }
  const allMessages = [...messages, ...applicabilityMessages];

  return <VoucherLine {...voucher} messages={allMessages} text={text} />;
};

const VoucherLine: React.FC<{
  code: string;
  messages: ICartMessage[];
  minimumOrderValueAbsolute: IMoney | null;
  minimumOrderQuantityAbsolute: number | null;
  text: string;
  type: VoucherType | null;
  voucherAmount: IMoney | null;
  voucherPercentage: IPercent | null;
}> = (props) => {
  const {cart} = useCartContext();
  const {
    code,
    messages,
    minimumOrderValueAbsolute,
    minimumOrderQuantityAbsolute,
    text,
    type,
    voucherAmount,
    voucherPercentage,
  } = props;
  const voucherValue = type === "Fix" ? voucherAmount : voucherPercentage;
  const voucherName = voucherValue?.formatted ? `${voucherValue.formatted} (${text})` : text;

  return (
    <div key={`voucher_${code}`} className={styles.list__row}>
      <div>
        <div className={styles.voucherName}>
          {messages.length ? (
            <span className={`Svg Svg-Icon-Caution ${styles.svgIconCautionOutline}`} />
          ) : (
            <FaCheck className={styles.checkIcon} color={"#6bb528"} />
          )}
          <strong>{voucherName}</strong>
        </div>
        <div className={styles.voucherRemove}>
          <span
            className={styles.list__row__delete}
            onClick={async () => {
              await cart.removeVoucher(code);
            }}
            style={{cursor: "pointer"}}
          >
            <FaTrashAlt />
          </span>
        </div>
      </div>
      {messages.map((msg) => (
        <VoucherItemMessage
          key={`${code}_${msg.code}`}
          currentVoucherCode={code}
          minimumOrderValue={minimumOrderValueAbsolute?.formatted || ""}
          minimumQuantity={minimumOrderQuantityAbsolute}
          msg={msg}
          voucherTitle={text}
        />
      ))}
    </div>
  );
};

const VoucherItemMessage: React.FC<{
  currentVoucherCode?: string;
  minimumOrderValue: string;
  minimumQuantity: number | null;
  msg: ICartMessage;
  voucherTitle: string;
}> = (prop) => {
  const {currentVoucherCode, minimumOrderValue, minimumQuantity, msg, voucherTitle} = prop;
  const {t} = useTranslation("cart");
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const message = t(msg.code, {
    minimumOrderValue,
    minimumQuantity,
    voucherCode: currentVoucherCode,
  });
  const textDivRef = useRef<HTMLDivElement>(null);

  const WhyNotLink: React.FC = () =>
    msg.code === NOT_ALL_ITEMS_VOUCHER_APPLICABLE || msg.code === NO_VOUCHER_APPLICABLE_ITEM_FOUND ? (
      <React.Fragment>
        {". "}
        <a
          href="#"
          onClick={(evt: React.MouseEvent<HTMLAnchorElement>) => {
            evt.preventDefault();
            setPopupVisible(true);
          }}
        >
          {t("Warum nicht?")}
        </a>
      </React.Fragment>
    ) : null;

  return (
    <div key={msg.code} ref={textDivRef} className={styles.list__row__text}>
      <div className={styles.voucherCaution} />
      <div className={styles.voucherWarning}>
        <ApplicableVouchersModal
          closeFunction={() => setPopupVisible(false)}
          isVisible={popupVisible}
          voucherTitle={voucherTitle}
        />
        <p>
          {message}
          <WhyNotLink />
        </p>
      </div>
    </div>
  );
};

/**
 * @component
 */
export default VoucherBlock;

const NO_VOUCHER_APPLICABLE_ITEM_FOUND = "NO_VOUCHER_APPLICABLE_ITEM_FOUND";
const NOT_ALL_ITEMS_VOUCHER_APPLICABLE = "NOT_ALL_ITEMS_VOUCHER_APPLICABLE";
export const VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED = "VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED";
export const VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED = "VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED";
